<template>
  <div class="channel-limits-container">
    <div class="title-icon-container">
      <span class="channel-limit-title">{{ title }}</span>
      <img v-if="channel === 'whatsapp'" :src="require('@/assets/icons/whatsapp-marca-0-l20.svg')" alt="Logo whatsapp" />
      <mf-icon v-else fill color="marca-0-l20" :icon="icon" size="20" />
    </div>

    <div class="clients-container">
      <clients-range-slider :channel="channel" :segments="campaign.segments" />
    </div>

    <div class="dispatch-configure-container">
      <span class="text-cinza-d10 weight-600">Quantidade máxima de disparos</span>
      <div class="bottom-line">
        <dispatch-limit-input
          v-model="dispatchLimit"
          :initial-limit="initialLimit"
          :suffix="inputSuffix"
          :error="dispatchError.length > 0"
          :error-messages="dispatchError"
          :hint="dispatchHint"
          :persistent-hint="!isCreatingNewCampaign"
          :disabled="disabled"
          @blur="clearErrors"
        />
        <div v-if="['sms', 'email'].includes(channel)" class="available-balance">
          <span class="text-marca-0-l20">
            Saldo disponível: <span class="weight-600">{{ localeString(availableBalance, 0) }}</span>
          </span>
        </div>
        <div v-if="dispatchType === 'DAILY'" class="d-flex flex-column ml-9">
          <span class="text-cinza-0">
            Total de dias da campanha: <span class="text-cinza-d10 weight-600">{{ campaignDays }}</span>
          </span>
          <span class="text-cinza-0">
            Quantidade máxima da campanha: <span class="text-marca-1-d30 weight-600">{{ localeString(campaignMaxQuantity, 0) }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { mapActions, mapGetters } from 'vuex'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'

export default {
  name: 'ChannelLimits',
  components: {
    ClientsRangeSlider: () => import('./ClientsRangeSlider.vue'),
    DispatchLimitInput: () => import('./DispatchLimitInput.vue'),
  },
  mixins: [mariaCampaign],
  model: {
    prop: 'value',
    event: 'update:dispatchLimit',
  },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (value) => ['sms', 'whatsapp', 'push', 'email'].includes(value),
    },
    availableBalance: {
      type: Number,
      default: 0,
    },
    value: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    initialLimit: 0,
    dispatchError: '',
  }),
  computed: {
    ...mapGetters(['daysOnOffers', 'campaignDays']),
    dispatchType() {
      return this.campaign.messages_dispatcher.budget.limiter
    },
    scheduleSettings() {
      return this.campaign?.messages_dispatcher?.settings?.schedule_settings
    },
    numberOfActive() {
      return this.offerCalendarDays.filter((day) => day.active).length
    },
    offerCalendarDays() {
      let days = []
      let startDate = DateTime.fromISO(this.campaign.initial_date)
      const endDate = DateTime.fromISO(this.campaign.final_date)
      while (startDate <= endDate) {
        days.push(this.createDayObject(startDate))
        startDate = startDate.plus({ days: 1 })
      }
      return days
    },
    dispatchLimit: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('update:dispatchLimit', value)
      },
    },
    title() {
      switch (this.channel) {
        case 'sms':
          return 'SMS'
        case 'whatsapp':
          return 'WhatsApp'
        case 'push':
          return 'Push Aplicativo'
        case 'email':
          return 'E-mail'
        default:
          return ''
      }
    },
    icon() {
      switch (this.channel) {
        case 'sms':
          return 'comment'
        case 'push':
          return 'phone_iphone'
        case 'email':
          return 'mail'
        default:
          return ''
      }
    },
    dispatchHint() {
      if (!this.isCreatingNewCampaign && this.isMariaCampaignDraft) {
        return `Qtd. mínima de disparos: ${0}.`
      } else if (!this.isCreatingNewCampaign && !this.isMariaCampaignDraft) {
        return `Qtd. mínima de disparos: ${this.localeString(this.initialLimit, 0)}.`
      }
      return ''
    },
    inputSuffix() {
      if (this.dispatchType === 'DAILY') {
        return 'por dia'
      }
      return 'geral'
    },
    settingsModel() {
      return this.campaign.messages_dispatcher.settings.model
    },
    daysOnOffersMaria() {
      return this.settingsModel === 'MARIA' && this.campaignHasOffers ? this.numberOfActive : this.campaignDays
    },
    campaignMaxQuantity() {
      return {
        MARIA: () => this.daysOnOffersMaria * this.dispatchLimit,
        RECURRENT: () => this.totalSumRecurrentDays() * this.dispatchLimit,
        STATIC_DATE: () => this.scheduleSettings.length * this.dispatchLimit,
      }[this.settingsModel]()
    },
  },
  watch: {
    dispatchType() {
      this.dispatchLimit = 0
      this.clearErrors()
    },
  },
  async mounted() {
    this.initialLimit = this.dispatchLimit

    this.setLoadingAction(true)
    try {
      if (this.campaignHasOffers) {
        const { offerScheduleIds, franchiseOfferScheduleIds } = this.getOfferSchedulesIds()
        await this.getDaysOnOffers({
          offers_schedule_ids: offerScheduleIds,
          franchise_offers_schedule_ids: franchiseOfferScheduleIds,
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      this.setLoadingAction(false)
    }
  },
  methods: {
    ...mapActions(['getDaysOnOffers']),
    getOfferSchedulesIds() {
      let offerScheduleIds = []
      let franchiseOfferScheduleIds = []
      this.campaign.offer_schedules.forEach((offer) => {
        offer.from_franchise
          ? franchiseOfferScheduleIds.push(offer.offer_schedule_id || offer._id || offer?.offer_schedule?._id)
          : offerScheduleIds.push(offer.offer_schedule_id || offer._id || offer?.offer_schedule?._id)
      })
      return { offerScheduleIds, franchiseOfferScheduleIds }
    },
    createDayObject(date) {
      const dayString = date.toISODate()
      const active = this.daysOnOffers.days.find((d) => DateTime.fromISO(d.day).toISODate() === dayString)?.active || false
      return {
        date: dayString,
        active,
      }
    },
    clearErrors() {
      this.dispatchError = ''
    },
    countSpecificWeekday(startDate, endDate, dayOfWeek) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      end.setDate(end.getDate() + 1)

      const daysMap = {
        domingo: 0,
        'segunda-feira': 1,
        'terça-feira': 2,
        'quarta-feira': 3,
        'quinta-feira': 4,
        'sexta-feira': 5,
        sábado: 6,
      }

      const targetDay = daysMap[dayOfWeek.toLowerCase()]
      if (targetDay === undefined) throw new Error('Dia da semana inválido')

      let count = 0
      /* eslint-disable no-unmodified-loop-condition */
      while (start <= end) {
        if (start.getDay() === targetDay) count++
        start.setDate(start.getDate() + 1)
      }

      return count
    },
    countWeekDays() {
      return this.scheduleSettings?.reduce((acc, { week_day }) => {
        acc[week_day] = (acc[week_day] || 0) + 1
        return acc
      }, {})
    },
    totalSumRecurrentDays() {
      return Object.keys(this.countWeekDays()).reduce((sum, day) => {
        return sum + this.countSpecificWeekday(this.campaign?.initial_date, this.campaign?.final_date, day)
      }, 0)
    },
    validate() {
      let isValid = true
      this.clearErrors()

      if (!this.isCreatingNewCampaign && !this.isMariaCampaignDraft && this.dispatchLimit < this.initialLimit) {
        this.dispatchError = `Quantidade não pode ser menor que ${this.initialLimit}.`
        isValid = false
      }

      return isValid
    },
  },
}
</script>

<style lang="scss" scoped>
.channel-limits-container {
  display: flex;
  flex-direction: column;
  gap: 24px;

  .title-icon-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;

    .channel-limit-title {
      color: $marca-0-l20;
      font-size: 18px;
      font-weight: 700;
    }
  }

  .clients-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .dispatch-configure-container {
    font-size: 14px;

    .bottom-line {
      margin-top: 8px;
      display: flex;
      flex-direction: row;
      gap: 20px;

      .available-balance {
        padding: 10px 12px;
        max-width: 220px;
        height: 40px;
        border-radius: 8px;
        background: rgba(210, 233, 255, 0.5);
        display: flex;
        justify-content: center;
      }
    }
  }

  .text-cinza-d10 {
    color: $cinza-d10;
  }

  .weight-600 {
    font-weight: 600;
  }

  .text-cinza-0 {
    color: $cinza-0;
  }

  .text-marca-0-l20 {
    color: $marca-0-l20;
  }

  .text-marca-1-d30 {
    color: $marca-1-d30;
  }
}
</style>
